import React, { useCallback, useMemo } from 'react'
import createBbox from '@turf/bbox'
import styled from 'styled-components'
import { ButtonLink, ButtonLinkAsSimpleText } from '~/prix/react/components/button'
import PolygonEditor, {
  PolygonValidationResult,
  usePolygonEditor,
} from '~/components/googleMap/polygonEditor'
import useGoogleMaps from '~/prix/react/hooks/googleMaps'
import GoogleMap from '~/components/googleMap/map'
import { Feature, Polygon } from '@turf/helpers'
import objectHash from 'object-hash'

const Wrapper = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  height: 500px;
  max-height: 600px;

  > main {
    border-radius: 8px;
    border-bottom: 0px;
    overflow: hidden;
  }

  .bottom {
    display: flex;
    flex-direction: row;
    padding: 16px;
    justify-content: space-between;
    align-items: center;

    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}px) {
      flex-direction: column;
    }

    .right {
      display: flex;
      flex: 1;
      justify-content: flex-end;
      align-items: baseline;
    }
  }
`

const ErrorMessage = styled.p`
  color: red;
  font-size: 12px;
  flex: 1;
  margin: 0;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}px) {
    margin-top: 0;
    margin-bottom: 10px;
  }
`

export type DemarcationType = 'polygon'
export type DemarcationPoint = google.maps.LatLngLiteral & {
  id: string
}

export type FeaturePolygonWithHash = Feature<
  Polygon,
  {
    hash: string
  }
>

export default function ModalSpaceDemarcation({
  onCloseModalDemarcation,
  setPolygonDemarcation,
  polygonDemarcation,
}: {
  onCloseModalDemarcation: () => void
  setPolygonDemarcation: (polygon: FeaturePolygonWithHash | null) => void
  polygonDemarcation: FeaturePolygonWithHash | null
}) {
  const { google } = useGoogleMaps()

  const brazilCenter = useMemo(
    () => ({
      lat: -13.182533103756843,
      lng: -52.89874109295445,
    }),
    [],
  )

  const polygon = usePolygonEditor(polygonDemarcation ?? null)
  const handleConfirm = useCallback(() => {
    if (!polygon.polygon) {
      return
    }
    const hash = objectHash(polygon.polygon.geometry.coordinates)
    setPolygonDemarcation({
      ...polygon.polygon,
      properties: {
        hash,
      },
    })
    onCloseModalDemarcation()
  }, [polygon.polygon])

  const handleCancel = () => {
    onCloseModalDemarcation()
  }

  const onReset = useCallback(() => {
    if (polygon.points.length) {
      polygon.onReset()
    }
    setPolygonDemarcation(null)
  }, [polygon.points.length])

  const mapLocation = useMemo(() => {
    if (!google || !polygonDemarcation) {
      return {
        center: brazilCenter,
        zoom: 4,
      }
    }
    const bbox = createBbox(polygonDemarcation)
    const bounds: google.maps.LatLngBoundsLiteral = {
      west: bbox[0],
      south: bbox[1],
      east: bbox[2],
      north: bbox[3],
    }
    return {
      bounds,
    }
  }, [google, polygonDemarcation])
  return (
    <Wrapper>
      <GoogleMap onClick={polygon.onMapClick} mapType='hybrid' {...mapLocation}>
        <PolygonEditor {...polygon} />
      </GoogleMap>

      <div className='bottom'>
        <ErrorMessage>
          {(polygon.polygonIsValid as PolygonValidationResult).errorMessage}
        </ErrorMessage>
        <div className='right'>
          <ButtonLinkAsSimpleText onGo={onReset} isDisabled={!onReset} withoutMargin>
            Limpar
          </ButtonLinkAsSimpleText>

          <ButtonLinkAsSimpleText style={{ marginRight: '7px' }} onGo={handleCancel}>
            Cancelar
          </ButtonLinkAsSimpleText>
          <ButtonLink
            onGo={handleConfirm}
            isDisabled={
              !polygon.polygon ||
              (polygon.polygonIsValid as PolygonValidationResult).status === false
            }
          >
            Selecionar
          </ButtonLink>
        </div>
      </div>
    </Wrapper>
  )
}
